import { Link } from "react-router-dom";
import type { CalendarView } from "./calendarTypes";
import "./CalendarRefHeader.css";

type Props = {
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  dailyBannerWeekday: string;
  dailyBannerDmY: string;
  weekAnchorIso: string;
  onWeekAnchorChange: (iso: string) => void;
  weekRangeLabel: string;
  monthTitle: string;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onThisWeek: () => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onThisMonth: () => void;
  dailyAnchorIso: string;
  onDailyAnchorChange: (iso: string) => void;
  onPrevDay: () => void;
  onNextDay: () => void;
  onToday: () => void;
  isDailyToday: boolean;
  onOpenMonthly: () => void;
  toolbarCreateSlot?: { date: string; startHHmm: string; endHHmm: string } | null;
  onToolbarCreate?: () => void;
  showScheduleSettings?: boolean;
  onOpenScheduleSettings?: () => void;
};

export function CalendarRefHeader({
  view,
  onViewChange,
  dailyBannerWeekday,
  dailyBannerDmY,
  weekAnchorIso,
  onWeekAnchorChange,
  weekRangeLabel,
  monthTitle,
  onPrevWeek,
  onNextWeek,
  onThisWeek,
  onPrevMonth,
  onNextMonth,
  onThisMonth,
  dailyAnchorIso,
  onDailyAnchorChange,
  onPrevDay,
  onNextDay,
  onToday,
  isDailyToday,
  onOpenMonthly,
  toolbarCreateSlot,
  onToolbarCreate,
  showScheduleSettings,
  onOpenScheduleSettings,
}: Props) {
  return (
    <>
      <nav className="kp-cal-ref-crumb" aria-label="Konum">
        <Link to="/dashboard">Kontrol Paneli</Link>
        <span className="kp-cal-ref-crumb__sep">/</span>
        <span className="kp-cal-ref-crumb__current">Randevu Takvimi</span>
        <span className="kp-cal-ref-build-id" title="Rezervasyon ayarlari paneli">
          v7
        </span>
      </nav>

      <header className="kp-cal-ref-head">
        <h1 className="kp-cal-ref-head__title">Direksiyon Dersi Takvimi</h1>
        <div className="kp-cal-ref-head__actions">
          {showScheduleSettings && onOpenScheduleSettings && (
            <button
              type="button"
              className="kp-cal-ref-settings-btn"
              onClick={onOpenScheduleSettings}
            >
              Rezervasyon Ayarları
            </button>
          )}
          <div className="kp-cal-ref-head__views" role="group" aria-label="Görünüm">
          <button
            type="button"
            className={`kp-cal-ref-head__view${view === "daily" ? " is-active" : ""}`}
            onClick={() => onViewChange("daily")}
          >
            Günlük
          </button>
          <button
            type="button"
            className={`kp-cal-ref-head__view${view === "weekly" ? " is-active" : ""}`}
            onClick={() => onViewChange("weekly")}
          >
            Haftalık
          </button>
          <button
            type="button"
            className={`kp-cal-ref-head__view kp-cal-ref-head__view--alt${view === "monthly" ? " is-active" : ""}`}
            onClick={onOpenMonthly}
            title="Ay özeti"
          >
            Aylık
          </button>
          </div>
        </div>
        <div className="kp-cal-ref-head__nav" role="group" aria-label="Tarih">
          {view === "daily" && (
            <>
              <button type="button" className="kp-cal-ref-nav-btn" onClick={onPrevDay}>
                « Önceki Gün
              </button>
              <label className="kp-cal-ref-date">
                <span className="kp-sr-only">Gün</span>
                <input
                  type="date"
                  value={dailyAnchorIso}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) onDailyAnchorChange(v);
                  }}
                />
              </label>
              <button type="button" className="kp-cal-ref-nav-btn kp-cal-ref-nav-btn--today" onClick={onToday}>
                Bugün
              </button>
              <button type="button" className="kp-cal-ref-nav-btn" onClick={onNextDay}>
                Sonraki Gün »
              </button>
            </>
          )}
          {view === "weekly" && (
            <>
              <button type="button" className="kp-cal-ref-nav-btn" onClick={onPrevWeek}>
                « Önceki Hafta
              </button>
              <label className="kp-cal-ref-date">
                <span className="kp-sr-only">Hafta</span>
                <input
                  type="date"
                  value={weekAnchorIso}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) onWeekAnchorChange(v);
                  }}
                />
              </label>
              <button type="button" className="kp-cal-ref-nav-btn kp-cal-ref-nav-btn--today" onClick={onThisWeek}>
                Bu Hafta
              </button>
              <button type="button" className="kp-cal-ref-nav-btn" onClick={onNextWeek}>
                Sonraki Hafta »
              </button>
            </>
          )}
          {view === "monthly" && (
            <>
              <button type="button" className="kp-cal-ref-nav-btn" onClick={onPrevMonth}>
                « Önceki ay
              </button>
              <button type="button" className="kp-cal-ref-nav-btn kp-cal-ref-nav-btn--today" onClick={onThisMonth}>
                Bu ay
              </button>
              <button type="button" className="kp-cal-ref-nav-btn" onClick={onNextMonth}>
                Sonraki ay »
              </button>
            </>
          )}
        </div>
      </header>

      {view === "daily" && (
        <div
          className={`kp-cal-ref-banner${isDailyToday ? " kp-cal-ref-banner--today" : " kp-cal-ref-banner--sub"}`}
        >
          <span className="kp-cal-ref-banner__text">
            {dailyBannerWeekday} | {dailyBannerDmY}
          </span>
          {isDailyToday && <span className="kp-cal-ref-banner__badge">BUGÜN</span>}
        </div>
      )}

      {view === "weekly" && (
        <div className="kp-cal-ref-banner kp-cal-ref-banner--sub">
          <span className="kp-cal-ref-banner__text">{weekRangeLabel}</span>
          <span className="kp-cal-ref-banner__hint">Aşağı kaydırarak haftanın tüm günlerini görün</span>
          {toolbarCreateSlot && onToolbarCreate && (
            <button
              type="button"
              className="kp-cal-ref-banner__cta"
              title={`${toolbarCreateSlot.date} ${toolbarCreateSlot.startHHmm}–${toolbarCreateSlot.endHHmm}`}
              onClick={onToolbarCreate}
            >
              + Yeni Randevu
            </button>
          )}
        </div>
      )}

      {view === "monthly" && (
        <div className="kp-cal-ref-banner kp-cal-ref-banner--sub">
          <span className="kp-cal-ref-banner__text">{monthTitle}</span>
          <span className="kp-cal-ref-banner__hint">Bir güne tıklayarak haftalık takvime geçin</span>
        </div>
      )}
    </>
  );
}
